// =============================================================================
// NOTIFICATION STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import { api } from '../api/client.js';
import type {
  Notification,
  NotificationType,
  NotificationPreferences,
} from '@chlann/shared';

// Re-export types for convenience
export type { Notification, NotificationType, NotificationPreferences };

interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  isPanelOpen: boolean;

  // Actions
  loadNotifications: (reset?: boolean) => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  loadUnreadCount: () => Promise<void>;
  loadPreferences: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  updatePreferences: (updates: Partial<NotificationPreferences>) => Promise<boolean>;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;

  // Socket handlers
  handleNewNotification: (notification: Notification) => void;
  handleBadgeUpdate: (count: number) => void;

  // UI state
  togglePanel: () => void;
  closePanel: () => void;
  clearError: () => void;
  reset: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  preferences: null,
  isLoading: false,
  hasMore: false,
  error: null,
  isPanelOpen: false,

  // Load notifications
  loadNotifications: async (reset = true) => {
    set({ isLoading: true, error: null, ...(reset ? { notifications: [] } : {}) });

    const result = await api.getNotifications({ limit: 50 });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoading: false,
        notifications: result.data.notifications as Notification[],
        hasMore: result.data.hasMore,
      });
    }
  },

  // Load more notifications (pagination)
  loadMoreNotifications: async () => {
    const { notifications, hasMore, isLoading } = get();
    if (!hasMore || isLoading) return;

    const lastNotification = notifications[notifications.length - 1];
    if (!lastNotification) return;

    set({ isLoading: true });

    const result = await api.getNotifications({
      limit: 50,
      before: lastNotification.createdAt,
    });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoading: false,
        notifications: [...notifications, ...(result.data.notifications as Notification[])],
        hasMore: result.data.hasMore,
      });
    }
  },

  // Load unread count
  loadUnreadCount: async () => {
    const result = await api.getUnreadNotificationCount();

    if (result.data) {
      set({ unreadCount: result.data.count });
    }
  },

  // Load preferences
  loadPreferences: async () => {
    const result = await api.getNotificationPreferences();

    if (result.data) {
      set({ preferences: result.data.preferences });
    }
  },

  // Mark notification as read
  markAsRead: async (id) => {
    const result = await api.markNotificationAsRead(id);

    if (result.error) {
      set({ error: result.error.message });
      return;
    }

    // Update local state
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },

  // Mark all as read
  markAllAsRead: async () => {
    const result = await api.markAllNotificationsAsRead();

    if (result.error) {
      set({ error: result.error.message });
      return;
    }

    // Update local state
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));
  },

  // Delete notification
  deleteNotification: async (id) => {
    const notification = get().notifications.find((n) => n.id === id);
    const result = await api.deleteNotification(id);

    if (result.error) {
      set({ error: result.error.message });
      return;
    }

    // Update local state
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount:
        notification && !notification.isRead
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
    }));
  },

  // Update preferences
  updatePreferences: async (updates) => {
    const result = await api.updateNotificationPreferences(updates);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    if (result.data) {
      set({ preferences: result.data.preferences });
    }

    return true;
  },

  // Subscribe to push notifications
  subscribeToPush: async () => {
    // Check if Push API is available
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      set({ error: 'Push notifications are not supported in this browser' });
      return false;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        set({ error: 'Notification permission denied' });
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Fetch VAPID public key via the api client (absolute API base). A raw
      // relative fetch hits the SPA rewrite in prod and returns index.html.
      const vapidResult = await api.getVapidPublicKey();
      const publicKey = vapidResult.data?.publicKey;
      if (vapidResult.error || !publicKey) {
        set({ error: 'Push notifications not configured on server' });
        return false;
      }

      // Convert URL-safe base64 to Uint8Array. iOS Safari + older
      // Chrome reject the raw base64 string form for applicationServerKey.
      const padding = '='.repeat((4 - (publicKey.length % 4)) % 4);
      const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawKey = atob(base64);
      const appServerKey = new Uint8Array(rawKey.length);
      for (let i = 0; i < rawKey.length; i++) appServerKey[i] = rawKey.charCodeAt(i);

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });

      const subscriptionJson = subscription.toJSON();

      if (!subscriptionJson.endpoint || !subscriptionJson.keys) {
        throw new Error('Invalid push subscription');
      }

      // Send subscription to server
      const result = await api.subscribeToPush({
        endpoint: subscriptionJson.endpoint,
        keys: {
          p256dh: subscriptionJson.keys.p256dh,
          auth: subscriptionJson.keys.auth,
        },
      });

      if (result.error) {
        set({ error: result.error.message });
        return false;
      }

      // Update preferences
      await get().updatePreferences({ enablePush: true });

      return true;
    } catch {
      set({ error: 'Failed to enable push notifications' });
      return false;
    }
  },

  // Unsubscribe from push notifications
  unsubscribeFromPush: async () => {
    try {
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get current subscription
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from server
      const result = await api.unsubscribeFromPush();

      if (result.error) {
        set({ error: result.error.message });
        return false;
      }

      // Update preferences
      await get().updatePreferences({ enablePush: false });

      return true;
    } catch {
      set({ error: 'Failed to disable push notifications' });
      return false;
    }
  },

  // Handle new notification from socket
  handleNewNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));
  },

  // Handle badge update from socket
  handleBadgeUpdate: (count) => {
    set({ unreadCount: count });
  },

  // Toggle panel visibility
  togglePanel: () => {
    set((state) => ({ isPanelOpen: !state.isPanelOpen }));
  },

  // Close panel
  closePanel: () => {
    set({ isPanelOpen: false });
  },

  // Clear error
  clearError: () => { set({ error: null }); },

  // Reset store on logout (shared-device privacy)
  reset: () =>
    { set({
      notifications: [],
      unreadCount: 0,
      preferences: null,
      isLoading: false,
      hasMore: false,
      error: null,
      isPanelOpen: false,
    }); },
}));
